import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "@emotion/styled";
import { Box, IconButton, Typography } from "@mui/material";
import { DefaultInput } from "src/components";
import { ButtonAddOutlined } from "src/components/Button/ButtonAddOutlined";
import { SubmitButton } from "src/components/Button/SubmitButton";
import { AvatarSelect } from "src/components/ImageSelect/AvatarSelect";
import { SectionHeader } from "src/components/SideBar/CalendarSideBar/components/CreateScheduleMenu/components/Popup/components";
import { dummyPic } from "src/constants/services";
import { PlusIcon } from "src/Icons";
import { TrashBinMenuIconFilled } from "src/Icons/TrashBinMenuIconFilled";
import {
  createCategories,
  removeCategories,
  updateCategories,
} from "src/store/reducers/categoriesSlice";
import {
  createCategoryTemplate,
  getCategoryTemplates,
  removeCategoryTemplate,
  updateCategoryTemplate,
} from "src/store/reducers/categoryTemplatesSlice";
import { closeModal, showModal } from "src/store/reducers/modalSlice";
import { isTitle } from "src/utils/validation";

const DeleteButton = styled(IconButton)({
  marginLeft: "8px",
  color: "#B9B9B9",
  "&:hover, &:active": {
    backgroundColor: "transparent",
  },
});

interface EditCategorySectionProps {
  studioId?: string;
  dataSection: any;
  onClose: () => void;
  onSubmit: () => void;
  validator: (image: string, categoryName: string, subcategoryNames: Array<any>) => boolean;
}

export function EditCategorySection ({
  studioId,
  dataSection,
  onClose,
  onSubmit,
  validator,
}: EditCategorySectionProps) {

  const dispatch = useDispatch();

  const [image, setImage] = useState<string>(dataSection?.image);

  const [title, setTitle] = useState<string>(dataSection?.title);

  const [categoryId, _setCategoryId] = useState<number>(dataSection.id)

  const [arraySubCategories, setArraySubCategories] = useState<any>(
    dataSection?.children
  );

  const formRef = useRef<any>();
  const avatarRef = useRef<any>();

  const handleChangeImage = (newImage: string) => {
    setImage(newImage);
  };

  const handleChangeTitle = (newValue: string) => {
    setTitle(newValue);
  };

  const handleChangeSubcategoryName = (newValue: string, id: number) => {
    setArraySubCategories(
      arraySubCategories &&
        arraySubCategories.map((subCategory: any) =>
          +subCategory.id === id
            ? { ...subCategory, title: newValue }
            : subCategory
        )
    );
  };

  const handleDeleteSubCategory = (id: number) => {
    setArraySubCategories(
      arraySubCategories &&
        arraySubCategories.filter((subCategory: any) => +subCategory.id !== id)
    );
  };

  const AddSubcategoryInput = useCallback(() => {
    const categoryFirstChildStyles = {
      borderTopLeftRadius: "6px",
      borderTopRightRadius: "6px",
    };
    const categoryLastChildStyles = {
      borderBottomLeftRadius: "6px",
      borderBottomRightRadius: "6px",
    };
    return (
      <Box
        sx={{
          marginTop: "32px",
        }}
      >
        <SectionHeader label="Add subcategory" />
        <Box>
          {arraySubCategories &&
            arraySubCategories.map((subCategory: any, index: number) => (
              <Box
                key={+subCategory.id}
                sx={{
                  display: "flex",
                }}
              >
                <DefaultInput
                  placeholder="subcategory"
                  initialValue={subCategory.title}
                  label={
                    arraySubCategories.length > 1
                      ? `Subcategory ${index + 1}`
                      : "Subcategory"
                  }
                  onInputValueChange={(newValue: string) =>
                    handleChangeSubcategoryName(newValue, +subCategory.id)
                  }
                  validationFn={isTitle}
                  sx={{
                    "&:first-of-type": categoryFirstChildStyles,
                    "&:last-of-type": categoryLastChildStyles,
                  }}
                />
                <DeleteButton
                  onClick={() => handleDeleteSubCategory(+subCategory.id)}
                  disableRipple
                >
                  <TrashBinMenuIconFilled />
                </DeleteButton>
              </Box>
            ))}
        </Box>
      </Box>
    );
  }, [arraySubCategories]);

  const handleAddSubcategory = () => {
    setArraySubCategories([
      ...arraySubCategories,
      {
        id: `${arraySubCategories.length + 1}`,
        title: "",
      },
    ]);
  };

  const handleUpdateCategoriesConditionaly = () => {

    const updateAction = studioId ? updateCategories : updateCategoryTemplate
    const createAction = studioId ? createCategories : createCategoryTemplate
    const deleteAction = studioId ? removeCategories : removeCategoryTemplate

    const conditionalParams = studioId ? { studioId } : {};

    const deletedSubCategories = dataSection?.children.filter(({ id }: any) => {
      const subCategory = arraySubCategories.find((cat: any) => {
        return cat.id === id;
      });
      return !subCategory;
    });

    if (dataSection?.title !== title || dataSection?.image !== image) {
      dispatch(
        updateAction({
          ...conditionalParams,
          categoriesId: dataSection?.id,
          title: dataSection?.title !== title ? title : undefined,
          ...(dataSection?.image !== image ? { image } : {}),
          onSuccess: () => {
            onClose();
            dispatch(getCategoryTemplates());
            dispatch(
              showModal({
                modalType: 'SUCCESS',
                successText: 'Category successfully updated',
                okButtonTitle: 'Ok, I got it',
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              })
            );
          },
          onFailure: (error: any) => {
            dispatch(
              showModal({
                modalType: 'DELETE',
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: 'Ok, I got it',
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              })
            );
          },
        })
      );
    }

    if (arraySubCategories.length > 0) {
      const promises = arraySubCategories.map(({ title, id, image }: any) => {
        return new Promise((res, rej) => {
          const subCategoryToUpdate = dataSection?.children.find((cat: any) => cat.id === id);

          if (subCategoryToUpdate) {
            dispatch(
              updateAction({
                ...conditionalParams,
                categoriesId: id,
                title,
                onSuccess: () => {
                  res(title);
                },
                onFailure: () => {
                  rej();
                },
              })
            );
          } else {
            dispatch(
              createAction({
                ...conditionalParams,
                categoriesId: id,
                title,
                image: image || dummyPic,
                parentId: categoryId,
                onSuccess: () => {
                  res(title);
                },
                onFailure: () => {
                  rej();
                },
              })
            );
          }
        });
      });

      Promise.all(promises)
        .then(() => {
          onClose();
          dispatch(getCategoryTemplates());
          dispatch(
            showModal({
              modalType: 'SUCCESS',
              successText: 'Category successfully updated',
              okButtonTitle: 'Ok, I got it',
              onButtonClick: () => {
                dispatch(closeModal());
              },
            })
          );
        })
        .catch(() => {
          dispatch(
            showModal({
              modalType: 'DELETE',
              title: "cannot update subcategories",
              okButtonTitle: 'Ok, I got it',
              isButtonCancel: true,
              onButtonClick: () => {
                dispatch(closeModal());
              },
            })
          );
        });
    }

    if (deletedSubCategories.length > 0) {
      const promises = deletedSubCategories.map(({ id }: any) => {
        return new Promise((res, rej) => {
          dispatch(
            deleteAction({
              ...conditionalParams,
              categoriesId: id,
              onSuccess: () => {
                res(title);
              },
              onFailure: () => {
                rej();
              },
            })
          );
        });
      });

      Promise.all(promises)
        .then(() => {
          onClose();
          dispatch(getCategoryTemplates());
          dispatch(
            showModal({
              modalType: 'SUCCESS',
              successText: 'Category successfully updated',
              okButtonTitle: 'Ok, I got it',
              onButtonClick: () => {
                dispatch(closeModal());
              },
            })
          );
        })
        .catch(() => {
          dispatch(
            showModal({
              modalType: 'DELETE',
              title: "cannot delete subcategories",
              okButtonTitle: 'Ok, I got it',
              isButtonCancel: true,
              onButtonClick: () => {
                dispatch(closeModal());
              },
            })
          );
        });
    }
  }

  return (
    <>
      <Box
        sx={{
          margin: "32px 0 0",
        }}
      >
        <AvatarSelect
          ref={avatarRef}
          image={image}
          onImageChanged={handleChangeImage}
          sx={{
            width: 88,
            height: 88,
          }}
        />
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: "400",
            color: "#828282",
            marginTop: "16px",
            marginBottom: "30px",
          }}
        >
          Allowed file types
          : png, jpg, jpeg.
        </Typography>
      </Box>

      <Box
        sx={{
          marginBottom: "16px",
        }}
      >
        <form ref={formRef}>
          <DefaultInput
            initialValue={title}
            label="Category name"
            placeholder="category"
            onInputValueChange={handleChangeTitle}
            validationFn={isTitle}
            sx={{
              borderTopRightRadius: "6px",
              borderTopLeftRadius: "6px",
              borderBottomRightRadius: "6px",
              borderBottomLeftRadius: "6px",
            }}
          />
          {AddSubcategoryInput()}
          <ButtonAddOutlined
            title="Add subcategory"
            onClick={handleAddSubcategory}
            leftIcon={<PlusIcon fontSize="inherit" />}
          />
        </form>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <SubmitButton
          variant="outlined"
          title="Cancel"
          onClick={onClose}
          sx={{
            width: "276px",
            borderRadius: "6px",
          }}
        />
        <SubmitButton
          title="Update category"
          disabled={!validator(image, title, arraySubCategories.map((sc: { title: any; }) => sc.title))}
          onClick={handleUpdateCategoriesConditionaly}
          sx={{
            width: "276px",
            borderRadius: "6px",
          }}
        />
      </Box>
    </>
  );
}
